/**
 * URL utility functions for EVOP Inspector Frontend
 *
 * Provides centralized URL generation and management
 */

/**
 * Get the base application URL.
 * The SPA is always served same-origin as the API (nginx serves the
 * frontend and proxies /api on the same host), so the browser origin IS
 * the app URL at runtime - in dev and prod. Resolved at runtime so it can
 * never be baked wrong at build time.
 */
export const getAppUrl = (): string => window.location.origin

/**
 * Get the API base path
 * Always uses relative path since nginx routes /api to backend
 */
export const getApiUrl = (): string => {
  return '/api'
}

/**
 * Generate full URL for any path (universal function)
 * This is the main function that other URL generators should use
 * 
 * @param path - The path to append to base URL (with or without leading slash)
 * @returns Full URL with base URL + normalized path
 */
export const generateFullUrl = (path: string): string => {
  const baseUrl = getAppUrl()
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${baseUrl}${normalizedPath}`
}

/**
 * Generate invitation URL for franchise partners
 * Format: {APP_URL}/invitation/{fp_code}/{token}
 */
export const generateInvitationUrl = (fpCode: string, token: string): string => {
  return generateFullUrl(`/invitation/${fpCode}/${token}`)
}

/**
 * Generate admin URL for internal navigation
 * Format: {APP_URL}/admin/{path}
 */
export const generateAdminUrl = (path: string): string => {
  return generateFullUrl(`/admin/${path}`)
}

/**
 * Generate WebSocket URL for real-time connections
 * Handles protocol switching (ws/wss based on http/https)
 */
export const generateWebSocketUrl = (path: string): string => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const host = window.location.host
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${protocol}//${host}${normalizedPath}`
}

/**
 * Generate Xero main application URL for working in the organization
 * Format: https://my.xero.com/!xkcD/Action/OrganisationLogin/{xero_shortcode}
 */
export const generateXeroMainUrl = (xeroShortCode: string): string => {
  return `https://my.xero.com/!xkcD/Action/OrganisationLogin/${xeroShortCode}`
}

/**
 * Generate Xero integrations management URL
 * Format: https://apps.xero.com/{xero_shortcode}/connected
 */
export const generateXeroIntegrationsUrl = (xeroShortCode: string): string => {
  return `https://apps.xero.com/${xeroShortCode}/connected`
}

/**
 * Generate Lightspeed main application URL
 * Format: https://{subdomain}.retail.lightspeed.app/
 */
export const generateLightspeedMainUrl = (storeCode: string): string => {
  return `https://${storeCode}.retail.lightspeed.app/`
}

/**
 * Generate Lightspeed personal tokens URL (for integrations management)
 * Format: https://{subdomain}.retail.lightspeed.app/setup/personal-tokens
 */
export const generateLightspeedTokensUrl = (storeCode: string): string => {
  return `https://${storeCode}.retail.lightspeed.app/setup/personal-tokens`
}

// Legacy function name kept for compatibility (still imported by the admin
// connections pages). The Lightspeed twin had no importers - removed.
export const generateXeroAppUrl = generateXeroIntegrationsUrl

export type ExceptionObjectType =
  | 'sales'
  | 'stock-management'
  | 'product-setup'
  | 'product-pricing'
  | 'systems'
  | 'client'
  | 'accounting'

/**
 * Map a row's ``data_domain`` (backend exception_types column) to the
 * inspector URL segment used under ``/inspect/{fp_code}/exceptions/``.
 *
 * Kept here (a leaf utils module) so platform-report columns can build
 * deep-link URLs from a row dict without importing the exception
 * registry - the registry already depends on every config, so configs
 * cannot import back from it without a circular dep.
 *
 * Returns null for unknown domains; callers fall back to the inspector
 * landing page.
 */
export const dataDomainToObjectType = (
  dataDomain: unknown,
): ExceptionObjectType | null => {
  switch (dataDomain) {
    case 'sale':
      return 'sales'
    case 'stock_management':
      return 'stock-management'
    case 'product_setup':
      return 'product-setup'
    case 'product_pricing':
      return 'product-pricing'
    case 'system':
      return 'systems'
    case 'client':
      return 'client'
    case 'accounting':
      return 'accounting'
    default:
      return null
  }
}

/* -------------------------------------------------------------------------
 * Inspector URL ref grammar: `<fp_code>-<letter>` (e.g. `auklm-a`).
 * The single home for the grammar; `generateInspectorUrl` builds every link.
 *
 * Stage 1: the tenure letter is cosmetic (one tenure per store, `fp_code`
 * still bare `auklm`), so we always emit `-a` and strip a trailing `-<letter>`
 * when parsing. The retired `<code>-<id>` form isn't recognised (`auklm-123`
 * 404s). Stage 2 (see `franchise-tenure-lineage`) renames `fp_code` to
 * `auklm-a` and stops appending; `/inspect/auklm-a` is unchanged.
 * ---------------------------------------------------------------------- */

/** Active tenure letter - always `'a'` in Stage 1; the seam Stage 2 upgrades. */
export const activeTenureLetter = (): string => 'a'

export interface InspectorRef {
  fpCode: string
  /** Dormant in Stage 1 (never populated); removed with the Stage-2 rename. */
  tenureId?: number
}

/**
 * Split a route segment into `{ fpCode }`, stripping a trailing cosmetic tenure
 * letter (`"aupsc-a"` -> `'aupsc'`). `-<digits>` (the retired id form) is left
 * intact so it 404s. Trimmed + lower-cased to mirror backend `fp_code_from_ref`.
 */
export const parseInspectorRef = (raw: string | undefined): InspectorRef => {
  const value = (raw ?? '').trim().toLowerCase()
  const match = /^(.+)-[a-z]$/.exec(value)
  return { fpCode: match ? match[1] : value }
}

/** Build the route segment: `<fp_code>-<active tenure letter>` (e.g. `auklm-a`). */
export const buildInspectorRef = (fpCode: string): string =>
  `${fpCode}-${activeTenureLetter()}`

export interface InspectorUrlOptions {
  /** Dormant in Stage 1 (ignored; the suffix is the active-tenure letter). Removed in Stage 2. */
  id?: number | null
  /** Sub-path under the FP segment, e.g. `'sales'` or `'/exceptions/sales'`. */
  subpath?: string
}

/** Build an inspector URL: `/inspect/<fp_code>-<letter><subpath>`. */
export const generateInspectorUrl = (
  fpCode: string,
  options: InspectorUrlOptions = {},
): string => {
  const { subpath } = options
  const ref = buildInspectorRef(fpCode)
  const sub = subpath ? (subpath.startsWith('/') ? subpath : `/${subpath}`) : ''
  return `/inspect/${ref}${sub}`
}

/**
 * Generate exception report URL for inspector
 * Format: /inspect/{fp_code}-{letter}/exceptions/{objectType}/{exception_code}
 *
 * @param fpCode - Franchise partner code
 * @param objectType - Type of exception (sales, stock-management, product-setup, product-pricing, systems, client, accounting)
 * @param exceptionCode - Optional specific exception code
 * @param _id - Dormant (ignored in Stage 1; kept so call sites compile)
 * @returns Relative URL path for the exception report
 */
export const generateExceptionUrl = (
  fpCode: string,
  objectType: ExceptionObjectType,
  exceptionCode?: string,
  _id?: number | null,
): string => {
  const subpath = exceptionCode
    ? `/exceptions/${objectType}/${exceptionCode}`
    : `/exceptions/${objectType}`
  return generateInspectorUrl(fpCode, { subpath })
}

/**
 * Generate an admin franchise-partner URL, addressed by the per-tenure
 * `franchise_partners.id` (admin is id-only - no fp_code form).
 * `generateAdminFpUrl(123)` -> `/admin/franchise-partners/123/edit`;
 * `generateAdminFpUrl(123, 'edit#users')` and `generateAdminFpUrl(123,
 * 'connections')` for the other admin sub-pages.
 */
export const generateAdminFpUrl = (
  id: number | string,
  sub: string = 'edit',
): string => `/admin/franchise-partners/${id}/${sub}`

/**
 * Inspector deep-link for a cross-FP report/grid row - the shared `link.builder`
 * for report cells. Points at the per-FP exception view when the row (or an
 * override) has both an object_type (`data_domain`) and an `exception_code`,
 * else the inspector landing. Keyed on `row.fp_code` only.
 */
export const buildRowInspectorLink = (
  row: Record<string, any>,
  objectType?: ExceptionObjectType | null,
  exceptionCode?: string | null,
): string | undefined => {
  const fpCode = row.fp_code as string | undefined
  if (!fpCode) return undefined
  const code = exceptionCode ?? (row.exception_code as string | undefined)
  const type = objectType ?? dataDomainToObjectType(row.data_domain)
  if (code && type) return generateExceptionUrl(fpCode, type, code)
  return generateInspectorUrl(fpCode)
}